import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { parseISO } from "date-fns/parseISO";

import tenantChanged from "~/store/tenantChanged";
import { type RowShippingUnit } from "~fulfill/inventory/stock/helpers/rowShippingUnit";
import resolveRefusedLineIds, { type LineRefusal } from "~mobile/engage/count/helpers/postRefusal";

// === Types ===

// Activity log entry
export interface ActivityLogEntry {
	action: "count" | "adjust" | "move" | "regrade" | "scrap";
	comment?: string;
	itemId: string;
	itemName: string;
	locationName: string;
	quantity: number;
	recordedBy: string;
	timestamp: string;
}

export interface CountedBatch {
	code: null | string;
	quantity: number;
}

// A single counted item stored per location
export interface UnpostedItem {
	batches?: CountedBatch[];
	batchTracked?: boolean;
	itemId: string;
	lastScannedAt?: number;
	name: string;
	note?: string;
	quantity: null | number;
	sku: string;
	unit?: null | RowShippingUnit;
	upc: string;
}

export type CountedRow = UnpostedItem & { quantity: number };

export type EntryUnit = "eaches" | "shippingUnit";

// Cached location option for offline use
export interface CachedLocationOption {
	address: string;
	customerName: string;
	id: string;
	name: string;
}

// Cached item summary for offline use
export interface CachedItemSummary {
	absorbedSkus?: string[];
	absorbedUpcs?: string[];
	batchTracked?: boolean;
	erpId?: string;
	itemId: string;
	name: string;
	sku: string;
	unit?: null | RowShippingUnit;
	upc: string;
}

export type DraftItemSnapshot = Pick<CachedItemSummary, "batchTracked" | "itemId" | "name" | "sku" | "unit" | "upc">;

export interface ManualEntryDraft {
	entryUnit?: EntryUnit;
	itemId: null | string;
	itemSnapshot?: DraftItemSnapshot;
	note: string;
	quantity: number | "";
}

export interface CountedLine {
	batches?: CountedBatch[];
	itemId: string;
	itemName: string;
	note?: string;
	quantity: number;
	sku: string;
}

export interface PendingCountPost {
	idempotencyKey: string;
	items: CountedLine[];
	locationId: string;
	locationName: string;
	queuedAt: string;
	recordedBy: string;
}

export interface PostReplayEvent {
	locationId: string;
	locationName: string;
	outcome: "failed" | "posted";
}

export interface PostFailure {
	itemIds: string[];
	refusalCode: null | string;
	replayed?: boolean;
}

// Cached history entry for offline use
export interface CachedHistoryEntry {
	id: string;
	idempotencyKey?: null | string;
	itemCount: number;
	items: CountedLine[];
	locationId: string;
	locationName: string;
	postedAt: string;
	recordedBy: string;
}

export interface CachedHistoryUpdate {
	entries: CachedHistoryEntry[];
	refreshedAt?: string;
}

export interface EngageState {
	activityLog: ActivityLogEntry[];
	/** Cached history entries for offline use */
	cachedHistory?: CachedHistoryEntry[];
	/** Cached items for offline use */
	cachedItems?: CachedItemSummary[];
	/** Cached customer locations for offline use */
	cachedLocations?: CachedLocationOption[];
	/** Remembers if user had camera scanning enabled within quick scan */
	cameraScanMode: boolean;
	isOffline: boolean;
	/** Last time history was refreshed from server */
	lastHistoryRefresh?: string;
	lastPostReplay?: PostReplayEvent | null;
	manualEntryDrafts?: Record<string, ManualEntryDraft>;
	pendingPosts?: Record<string, PendingCountPost>;
	postActionKeys: Record<string, Record<string, string>>;
	postFailures?: Record<string, PostFailure>;
	/** Remembers if user prefers quick scan or regular mode */
	quickScanMode: boolean;
	recentLocationIds?: string[];
	restoredLocationId?: string;
	selectedLocationId?: string;
	/** Customer name for the selected location */
	selectedLocationCustomerName?: string;
	/** Currently selected location display name */
	selectedLocationName?: string;
	/** Unposted counted items keyed by locationId */
	unpostedCounts: Record<string, UnpostedItem[]>;
}

const initialState: EngageState = {
	activityLog: [],
	cachedHistory: [],
	cachedItems: [],
	cachedLocations: [],
	cameraScanMode: false,
	isOffline: false,
	lastPostReplay: null,
	pendingPosts: {},
	postActionKeys: {},
	quickScanMode: false,
	recentLocationIds: [],
	unpostedCounts: {}
};

const ENGAGE_RECENT_STORE_LIMIT = 5;

/** Pure helper: filters activity log entries to those after the given cutoff date. */
export const filterActivityLogByDate = (entries: ActivityLogEntry[], cutoffDate: string): ActivityLogEntry[] =>
	entries.filter((entry) => entry.timestamp > cutoffDate);

export const toCachedHistoryEntry = (pending: PendingCountPost, postedAt: string): CachedHistoryEntry => ({
	id: pending.idempotencyKey,
	itemCount: pending.items.length,
	items: pending.items,
	locationId: pending.locationId,
	locationName: pending.locationName,
	postedAt,
	recordedBy: pending.recordedBy
});

export const CACHED_HISTORY_LIMIT = 50;

const parsePostedTime = (entry: CachedHistoryEntry): number => parseISO(entry.postedAt).getTime();

export const mergeCachedHistory = (
	cached: readonly CachedHistoryEntry[],
	incoming: readonly CachedHistoryEntry[]
): CachedHistoryEntry[] => {
	const incomingIdentities = new Set(incoming.flatMap((entry) => [entry.id, entry.idempotencyKey ?? entry.id]));
	const isSuperseded = ({ id, idempotencyKey }: CachedHistoryEntry): boolean =>
		incomingIdentities.has(id) || (idempotencyKey ? incomingIdentities.has(idempotencyKey) : false);
	return [...incoming, ...cached.filter((entry) => !isSuperseded(entry))]
		.sort((a, b) => parsePostedTime(b) - parsePostedTime(a))
		.slice(0, CACHED_HISTORY_LIMIT);
};

export const isCountedRow = (item: UnpostedItem): item is CountedRow => item.quantity !== null;

export const EMPTY_MANUAL_ENTRY_DRAFT: ManualEntryDraft = { itemId: null, note: "", quantity: "" };

const isEmptyManualEntryDraft = (draft: ManualEntryDraft): boolean =>
	draft.itemId === null && draft.quantity === "" && draft.note === "";

const takePendingPost = (state: EngageState, idempotencyKey: string): PendingCountPost | undefined => {
	const pending = state.pendingPosts?.[idempotencyKey];
	if (!pending || !state.pendingPosts) return undefined;
	delete state.pendingPosts[idempotencyKey];
	return pending;
};

const fingerprintLine = (item: UnpostedItem): string =>
	JSON.stringify([item.quantity, item.note ?? "", item.batches ?? []]);

const keepUnchangedLineMarks = (state: EngageState, locationId: string, items: readonly UnpostedItem[]) => {
	const failure = state.postFailures?.[locationId];
	if (!failure) return;
	if (items.length === 0) {
		delete state.postFailures?.[locationId];
		return;
	}
	const before = state.unpostedCounts[locationId] ?? [];
	failure.itemIds = failure.itemIds.filter((itemId) => {
		const previous = before.find((item) => item.itemId === itemId);
		const next = items.find((item) => item.itemId === itemId);
		return previous !== undefined && next !== undefined && fingerprintLine(previous) === fingerprintLine(next);
	});
};

const toRestoredLine = (line: CountedLine, cachedItem: CachedItemSummary | undefined): UnpostedItem => ({
	...(line.batches ? { batches: line.batches } : {}),
	batchTracked: cachedItem?.batchTracked,
	itemId: line.itemId,
	name: line.itemName,
	note: line.note,
	quantity: line.quantity,
	sku: line.sku,
	unit: cachedItem?.unit,
	upc: cachedItem?.upc ?? ""
});

const restoreRefusedPost = (state: EngageState, refused: PendingCountPost, refusal: LineRefusal | null) => {
	const current = state.unpostedCounts[refused.locationId] ?? [];
	const recountedItemIds = new Set(current.filter(isCountedRow).map((item) => item.itemId));
	const restored = refused.items
		.filter((line) => !recountedItemIds.has(line.itemId))
		.map((line) =>
			toRestoredLine(
				line,
				state.cachedItems?.find((cached) => cached.itemId === line.itemId)
			)
		);
	const restoredItemIds = new Set(restored.map((item) => item.itemId));
	state.unpostedCounts[refused.locationId] = [
		...current.filter((item) => !restoredItemIds.has(item.itemId)),
		...restored
	];
	if (!state.postFailures) state.postFailures = {};
	state.postFailures[refused.locationId] = {
		itemIds: resolveRefusedLineIds(
			refusal,
			refused.items.map((line) => line.itemId)
		),
		refusalCode: refusal?.code ?? null,
		replayed: true
	};
};

const appendCommittedPost = (state: EngageState, idempotencyKey: string): PendingCountPost | undefined => {
	const pending = takePendingPost(state, idempotencyKey);
	if (!pending) return undefined;
	state.cachedHistory = mergeCachedHistory(state.cachedHistory ?? [], [
		toCachedHistoryEntry(pending, new Date().toISOString())
	]);
	return pending;
};

export const engageSlice = createSlice({
	extraReducers: (builder) => {
		builder.addCase(tenantChanged, (state) => {
			state.recentLocationIds = [];
			state.selectedLocationId = undefined;
			state.selectedLocationCustomerName = undefined;
			state.selectedLocationName = undefined;
		});
	},
	initialState,
	name: "engage",
	reducers: {
		addActivityLog: (state, action: PayloadAction<ActivityLogEntry>) => {
			if (!state.activityLog) state.activityLog = [];
			state.activityLog.unshift(action.payload);
			if (state.activityLog.length > 500) {
				state.activityLog = state.activityLog.slice(0, 500);
			}
		},
		clearPostReplay: (state) => {
			state.lastPostReplay = null;
		},
		cleanupActivityLog: (state, action: PayloadAction<ActivityLogEntry[]>) => {
			state.activityLog = action.payload;
		},
		clearPostedCounts: (state, action: PayloadAction<string>) => {
			delete state.unpostedCounts[action.payload];
			delete state.postFailures?.[action.payload];
			delete state.postActionKeys[action.payload];
		},
		clearUnpostedCounts: (state, action: PayloadAction<string>) => {
			delete state.unpostedCounts[action.payload];
			delete state.postFailures?.[action.payload];
		},
		commitPendingPost: (state, action: PayloadAction<string>) => {
			appendCommittedPost(state, action.payload);
		},
		commitReplayedPost: (state, action: PayloadAction<string>) => {
			const committed = appendCommittedPost(state, action.payload);
			if (committed)
				state.lastPostReplay = {
					locationId: committed.locationId,
					locationName: committed.locationName,
					outcome: "posted"
				};
		},
		discardPendingPost: (state, action: PayloadAction<string>) => {
			takePendingPost(state, action.payload);
		},
		failPendingPost: (state, action: PayloadAction<{ idempotencyKey: string; refusal: LineRefusal | null }>) => {
			const { idempotencyKey, refusal } = action.payload;
			const refused = takePendingPost(state, idempotencyKey);
			if (!refused) return;
			state.lastPostReplay = { locationId: refused.locationId, locationName: refused.locationName, outcome: "failed" };
			restoreRefusedPost(state, refused, refusal);
		},
		setCachedItems: (state, action: PayloadAction<CachedItemSummary[] | null>) => {
			if (action.payload === null) {
				state.cachedItems = undefined;
				return;
			}
			state.cachedItems = action.payload;
		},
		setCachedLocations: (state, action: PayloadAction<CachedLocationOption[] | null>) => {
			if (action.payload === null) {
				state.cachedLocations = undefined;
				return;
			}
			state.cachedLocations = action.payload;
		},
		setCameraScanMode: (state, action: PayloadAction<boolean | null>) => {
			if (action.payload === null) {
				state.cameraScanMode = false;
				return;
			}
			state.cameraScanMode = action.payload;
		},
		setPostActionKeys: (
			state,
			action: PayloadAction<{ actionKeysBySignature: Record<string, string>; locationId: string }>
		) => {
			const { actionKeysBySignature, locationId } = action.payload;
			state.postActionKeys[locationId] = actionKeysBySignature;
		},
		setPostFailure: (state, action: PayloadAction<{ failure: PostFailure; locationId: string }>) => {
			const { failure, locationId } = action.payload;
			if (!state.postFailures) state.postFailures = {};
			state.postFailures[locationId] = failure;
		},
		setOfflineStatus: (state, action: PayloadAction<boolean | null>) => {
			if (action.payload === null) {
				state.isOffline = false;
				return;
			}
			state.isOffline = action.payload;
		},
		setQuickScanMode: (state, action: PayloadAction<boolean | null>) => {
			if (action.payload === null) {
				state.quickScanMode = false;
				return;
			}
			state.quickScanMode = action.payload;
		},
		setSelectedLocation: (
			state,
			action: PayloadAction<{
				customerName?: string;
				id: string;
				name?: string;
			} | null>
		) => {
			const payload = action.payload;
			state.restoredLocationId = undefined;
			if (payload === null) {
				state.selectedLocationId = undefined;
				state.selectedLocationCustomerName = undefined;
				state.selectedLocationName = undefined;
				return;
			}
			state.selectedLocationId = payload.id;
			state.selectedLocationCustomerName = payload.customerName;
			state.selectedLocationName = payload.name;
			state.recentLocationIds = [
				payload.id,
				...(state.recentLocationIds ?? []).filter((recentId) => recentId !== payload.id)
			].slice(0, ENGAGE_RECENT_STORE_LIMIT);
		},
		setUnpostedCounts: (state, action: PayloadAction<{ items: UnpostedItem[] | null; locationId: string }>) => {
			const { items, locationId } = action.payload;
			keepUnchangedLineMarks(state, locationId, items ?? []);
			if (!items || items.length === 0) {
				delete state.unpostedCounts[locationId];
			} else {
				state.unpostedCounts[locationId] = items;
			}
		},
		stagePendingPost: (state, action: PayloadAction<PendingCountPost>) => {
			if (!state.pendingPosts) state.pendingPosts = {};
			state.pendingPosts[action.payload.idempotencyKey] = action.payload;
		},
		updateCachedHistory: (state, action: PayloadAction<CachedHistoryUpdate>) => {
			const { entries, refreshedAt } = action.payload;
			state.cachedHistory = mergeCachedHistory(state.cachedHistory ?? [], entries);
			if (refreshedAt) state.lastHistoryRefresh = refreshedAt;
		},
		updateManualEntryDraft: (
			state,
			action: PayloadAction<{ changes: Partial<ManualEntryDraft>; locationId: string }>
		) => {
			const { changes, locationId } = action.payload;
			if (!state.manualEntryDrafts) state.manualEntryDrafts = {};
			const draft = { ...EMPTY_MANUAL_ENTRY_DRAFT, ...state.manualEntryDrafts[locationId], ...changes };
			if (isEmptyManualEntryDraft(draft)) delete state.manualEntryDrafts[locationId];
			else state.manualEntryDrafts[locationId] = draft;
		}
	}
});

export const {
	addActivityLog,
	cleanupActivityLog,
	clearPostedCounts,
	clearPostReplay,
	clearUnpostedCounts,
	commitPendingPost,
	commitReplayedPost,
	discardPendingPost,
	failPendingPost,
	setCachedItems,
	setCachedLocations,
	setCameraScanMode,
	setOfflineStatus,
	setPostActionKeys,
	setPostFailure,
	setQuickScanMode,
	setSelectedLocation,
	setUnpostedCounts,
	stagePendingPost,
	updateCachedHistory,
	updateManualEntryDraft
} = engageSlice.actions;

export default engageSlice.reducer;
